import React, { useCallback, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { DeviceSelector } from '@/components/shared/DeviceSelectorNew';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import LinearProgress from '@mui/material/LinearProgress';
import { FiRotateCcw } from 'react-icons/fi';
import { SquarePen } from 'lucide-react' 
import useGlobalDeviceSelector from '@/hooks/useGlobalDeviceSelector';
import { useDispatch } from 'react-redux';
import { setLoading } from '@/store/slices/appSlice';
import { apiService } from '@/utils/api';
import type { DeviceSettingsResponse, FilterPumpSettingsResponse, HeatingSettingsResponse, PumpSettingsResponse } from '@/types/deviceSettings';
import type { PhSettings, ChlorineSettings } from '@/types/chemistry';
import { isVSDPump } from '@/utils/utils';
import { AuxTypeCode } from '@/types/device';
import { hasAuxType } from '@/utils/chemistryUtils';

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
);

// 可编辑行：左侧 Label，右侧 Value + 编辑图标（图标最右）
const EditableRow: React.FC<{ label: string; value?: string | number | React.ReactNode; onEdit: () => void }> = ({ label, value, onEdit }) => (
  <div className="flex items-center justify-between py-1 gap-2">
    <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
    <div className="inline-flex items-center gap-2 shrink-0">
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{value ?? '-'}</span>
      <button
        type="button"
        className="inline-flex items-center text-primary-600 hover:text-primary-700"
        onClick={onEdit}
        aria-label={`Edit ${label}`}
      >
        <SquarePen className="h-4 w-4" />
      </button>
    </div>
  </div>
);

// 单位格式化工具
const formatKpa = (v?: number | null) => (v != null ? `${v} kpa` : '-');
const formatSeconds = (v?: number | null) => (v != null ? `${v} seconds` : '-');
const formatMinutes = (v?: number | null) => (v != null ? `${v} minutes` : '-');
const formatTimes = (v?: number | null) => (v != null ? `${v} times` : '-');
const formatAmp = (v?: number | null) => (v != null ? `${v} A` : '-');
const formatPercent = (v?: number | null) => (v != null ? `${v} %` : '-');
const formatLiters = (v?: number | null) => (v != null ? `${(v / 1000).toFixed(2)} L` : '-');
const formatPh = (v?: number | null) => (v != null ? `${(v / 10).toFixed(2)}` : '-');
const formatMlPerMin = (v?: number | null) => (v != null ? `${v} mL/min` : '-');
const formatDegrees = (v?: number | null) => (v != null ? `${v} degrees` : '-');
// 安全除法，避免分母为 0（参考 Android safeDivision）
const safeDivision = (numerator?: number | null, denominator?: number | null) => (!denominator ? 0 : (numerator ?? 0) / denominator);

const DRY_RUN_MIN_FW = '2.5.27';
const DRY_RUN_DISABLE_COMMAND = 1;
const DRY_RUN_ENABLE_COMMAND = 2;
const DRY_RUN_DISABLE_TOTAL_SECONDS = 600;

const compareVersion = (a?: string, b?: string): number => {
  if (!a || !b) return -1;
  const left = a.split('.');
  const right = b.split('.');
  const maxLen = Math.max(left.length, right.length);
  for (let i = 0; i < maxLen; i += 1) {
    const lv = Number(left[i] ?? 0);
    const rv = Number(right[i] ?? 0);
    if (lv > rv) return 1;
    if (lv < rv) return -1;
  }
  return 0;
};

const normalizeDryRunRemaining = (rawValue?: number | null): number => {
  const value = rawValue ?? 0;
  if (value <= 0 || value === DRY_RUN_ENABLE_COMMAND) return 0;
  if (value === DRY_RUN_DISABLE_COMMAND) return DRY_RUN_DISABLE_TOTAL_SECONDS;
  return Math.min(Math.max(value, 0), DRY_RUN_DISABLE_TOTAL_SECONDS);
};

const formatCountdown = (seconds: number): string => {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const remain = safe % 60;
  return `${minutes}:${remain.toString().padStart(2, '0')}`;
};

const DeviceSettingsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedDeviceId, onDeviceChange } = useGlobalDeviceSelector();

  const [data, setData] = React.useState<DeviceSettingsResponse | null>(null);
  // 编辑弹窗状态
  type PumpEditKey =
    | 'backwash_pressure'
    | 'no_flow_cutoff_time'
    | 'no_flow_retry_times'
    | 'minimum_pressure_cutoff'
    | 'minimum_flow_cutoff'
    | 'max_run_current'
    | 'pump_prime_time_100'
    | 'pump_start_speed'
    | 'min_allow_pump_speed'
    | 'spa_mode_pump_speed'
    | 'heater_pump_speed'
    | 'solar_heating_pump_speed'
    | 'cleaner_pump_speed';
  const [editOpen, setEditOpen] = React.useState<boolean>(false);
  const [editDraft, setEditDraft] = React.useState<string>('');
  const [editMeta, setEditMeta] = React.useState<{ key: PumpEditKey; label: string; unit: string } | null>(null);
  const [editError, setEditError] = React.useState<string>('');

  // Keys that should be validated as percentage (0–100 integer)
  const speedKeys = React.useMemo(() => new Set<PumpEditKey>([
    'pump_start_speed',
    'min_allow_pump_speed',
    'spa_mode_pump_speed',
    'heater_pump_speed',
    'solar_heating_pump_speed',
    'cleaner_pump_speed',
  ]), []);

  const validateValue = React.useCallback((raw: string, meta: { key: PumpEditKey } | null): string => {
    const value = raw.trim();
    if (!value) return 'Please enter a value';
    // Integers only (no decimals)
    if (!/^-?\d+$/.test(value)) return 'Integers only (no decimals)';
    const n = Number(value);
    if (meta && speedKeys.has(meta.key)) {
      if (n < 0 || n > 100) return 'Percentage must be an integer from 0 to 100';
    }
    return '';
  }, [speedKeys]);

  const fetchData = useCallback(async (deviceId: string, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      dispatch(setLoading(true));
    }
    try {
      const res = await apiService.get<DeviceSettingsResponse>(`/api/v1/iot-shadow/device/${deviceId}/deviceSettings`);
      setData(res);
    } catch (err) {
      console.error('Failed to fetch device settings:', err);
    } finally {
      if (!options?.silent) {
        dispatch(setLoading(false));
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (!selectedDeviceId) return;
    fetchData(selectedDeviceId);
  }, [selectedDeviceId, fetchData]);

  const handleDeviceChange = useCallback((deviceId: string) => {
    onDeviceChange(deviceId);
  }, [onDeviceChange]);

  const pump: FilterPumpSettingsResponse | null = data?.filterPumpSettings ?? null;
  const pumpSettings: PumpSettingsResponse | null = data?.pumpSettings ?? null;
  const pumpType: string | null = data?.pumpInfo?.pump_type ?? null;
  const dryRunRemainingFromPumpInfo = data?.pumpInfo?.dry_run_info?.protection_remaining_second ?? 0;
  const isVsd = useMemo(() => isVSDPump(pumpType ?? ''), [pumpType]);
  const isSplashMeVsdPump = useMemo(() => (pumpType ?? '').trim() === 'SplashMe VSD', [pumpType]);
  const isDryRunSupported = useMemo(
    () => compareVersion(data?.versionInfo?.firmware_version, DRY_RUN_MIN_FW) > 0,
    [data?.versionInfo?.firmware_version]
  );
  const equipmentTypeCodes = useMemo(
    () => (data?.auxAssign?.equipment ?? []).map((item) => item.type_code),
    [data?.auxAssign?.equipment]
  );
  const hasSpa = useMemo(
    () => equipmentTypeCodes.some((code) => [
      AuxTypeCode.SPA_ACTUATOR_SUCTION,
      AuxTypeCode.SPA_ACTUATOR_RETURN,
      AuxTypeCode.SPA_ACTUATOR_EXTRA_1,
      AuxTypeCode.SPA_ACTUATOR_EXTRA_2,
    ].includes(code)),
    [equipmentTypeCodes]
  );
  const hasHeater = useMemo(
    () => equipmentTypeCodes.some((code) => [
      AuxTypeCode.POOL_HEATER,
      AuxTypeCode.SPA_HEATER,
      AuxTypeCode.COMBINE_POOL_SPA_HEATER,
    ].includes(code)),
    [equipmentTypeCodes]
  );
  const hasSolar = useMemo(
    () => equipmentTypeCodes.some((code) => [
      AuxTypeCode.SOLAR_DEDICATED,
      AuxTypeCode.SOLAR_FILTER_PUMP,
    ].includes(code)),
    [equipmentTypeCodes]
  );
  const hasCleaner = useMemo(
    () => equipmentTypeCodes.some((code) => [
      AuxTypeCode.POOL_PRESSURE_CLEANER,
      AuxTypeCode.POOL_IN_FLOOR_CLEANING,
    ].includes(code)),
    [equipmentTypeCodes]
  );
  const [dryRunRemainingSeconds, setDryRunRemainingSeconds] = React.useState(0);
  const [isDryRunTogglePending, setIsDryRunTogglePending] = React.useState(false);
  const dryRunProtectionEnabled = dryRunRemainingSeconds === 0;
  const ph: PhSettings | null = (data?.phSettings ?? null) as PhSettings | null;
  const chlorine: ChlorineSettings | null = (data?.chlorineSettings ?? null) as ChlorineSettings | null;
  const heating: HeatingSettingsResponse | null = (data?.heatingSettings ?? null) as HeatingSettingsResponse | null;
  const generalPumpSettingKeys = React.useMemo(() => new Set<PumpEditKey>([
    'backwash_pressure',
    'no_flow_cutoff_time',
    'no_flow_retry_times',
    'minimum_pressure_cutoff',
    'minimum_flow_cutoff',
  ]), []);

  useEffect(() => {
    if (isDryRunTogglePending) return;
    const normalizedCountdown = dryRunRemainingFromPumpInfo > 0
      ? normalizeDryRunRemaining(dryRunRemainingFromPumpInfo)
      : normalizeDryRunRemaining(pump?.drycheck_disable);
    setDryRunRemainingSeconds(normalizedCountdown);
  }, [dryRunRemainingFromPumpInfo, pump?.drycheck_disable, selectedDeviceId, isDryRunTogglePending]);

  useEffect(() => {
    if (isDryRunTogglePending || !isDryRunSupported || dryRunRemainingSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setDryRunRemainingSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isDryRunSupported, dryRunRemainingSeconds, isDryRunTogglePending]);

  useEffect(() => {
    if (isDryRunTogglePending || !selectedDeviceId || !isDryRunSupported || dryRunRemainingSeconds <= 0) return;
    const pollTimer = window.setInterval(() => {
      fetchData(selectedDeviceId, { silent: true });
    }, 3000);
    return () => window.clearInterval(pollTimer);
  }, [selectedDeviceId, isDryRunSupported, dryRunRemainingSeconds, fetchData, isDryRunTogglePending]);
  
  // ===================== Heating Edit (独立于 Sanitisation / Filter Pump) =====================
  type HeatingEditKey = 'heater_cooldown' | 'spa_heater_cooldown' | 'solar_hysteresis_temp' | 'solar_hysteresis_time';
  const [heatEditOpen, setHeatEditOpen] = React.useState<boolean>(false);
  const [heatEditDraft, setHeatEditDraft] = React.useState<string>('');
  const [heatEditMeta, setHeatEditMeta] = React.useState<{ key: HeatingEditKey; label: string; unit: string } | null>(null);
  const [heatEditError, setHeatEditError] = React.useState<string>('');

  const validateHeatValue = React.useCallback((raw: string): string => {
    const value = raw.trim();
    if (!value) return 'Please enter a value';
    if (!/^-?\d+$/.test(value)) return 'Integers only (no decimals)';
    const n = Number(value);
    if (n < 0) return 'Value must be zero or positive';
    return '';
  }, []);

  const openHeatEdit = React.useCallback((meta: { key: HeatingEditKey; label: string; unit: string }, currentValue?: number | null) => {
    setHeatEditMeta(meta);
    const draft = currentValue != null ? String(currentValue) : '';
    setHeatEditDraft(draft);
    setHeatEditError(validateHeatValue(draft));
    setHeatEditOpen(true);
  }, [validateHeatValue]);

  const submitHeatingSetting = React.useCallback(async (deviceId: string, key: HeatingEditKey, value: number) => {
    const payload: Record<string, any> = {
      heater_cooldown: heating?.heater_cooldown ?? 180,
      spa_heater_cooldown: heating?.spa_heater_cooldown ?? 180,
      solar_hysteresis_temp: heating?.solar_hysteresis_temp ?? 5,
      solar_hysteresis_time: heating?.solar_hysteresis_time ?? 120,
      solar_tropical_mode: heating?.solar_tropical_mode ?? false,
      solar_collector_flush_time: heating?.solar_collector_flush_time ?? 0,
    };
    payload[key] = value;
    dispatch(setLoading(true));
    try {
      await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${deviceId}/setHeatingSettings`, payload);
    } finally {
      dispatch(setLoading(false));
    }
  }, [heating, dispatch]);

  const handleConfirmHeatEdit = React.useCallback(async () => {
    if (!selectedDeviceId || !heatEditMeta) {
      setHeatEditError('Missing device or edit item info');
      return;
    }
    const err = validateHeatValue(heatEditDraft);
    if (err) { setHeatEditError(err); return; }
    const parsed = Number(heatEditDraft);
    try {
      await submitHeatingSetting(selectedDeviceId, heatEditMeta.key, parsed);
      if (selectedDeviceId) await fetchData(selectedDeviceId);
    } finally {
      setHeatEditOpen(false);
    }
  }, [selectedDeviceId, heatEditMeta, heatEditDraft, submitHeatingSetting, validateHeatValue, fetchData]);

  
  
  // Check for presence of sanitization devices
  const hasPhDoser = useMemo(() => hasAuxType(data?.auxAssign, AuxTypeCode.PH_DOSER), [data?.auxAssign]);
  const hasSaltChlorinator = useMemo(() => hasAuxType(data?.auxAssign, AuxTypeCode.SALT_CHLORINATOR) || 
                                           hasAuxType(data?.auxAssign, AuxTypeCode.MINERAL_CHLORINATOR), 
                                    [data?.auxAssign]);
  const hasLiquidChlorine = useMemo(() => hasAuxType(data?.auxAssign, AuxTypeCode.LIQUID_CHLORINE), [data?.auxAssign]);
  
  // Define which settings to show based on device presence
  const phSettingIndexList = useMemo(() => hasPhDoser ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] : [], [hasPhDoser]);
  const chlorineSettingIndexList = useMemo(() => {
    if (hasSaltChlorinator && !hasLiquidChlorine) {
      return [0, 1]; // Only show mineral chlorinator settings
    } else if (!hasSaltChlorinator && hasLiquidChlorine) {
      return [2, 3, 4, 5, 6, 7, 8, 9]; // Only show liquid chlorine settings
    } else if (hasSaltChlorinator && hasLiquidChlorine) {
      return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]; // Show both mineral and liquid chlorine settings
    }
    return [];
  }, [hasSaltChlorinator, hasLiquidChlorine]);
  
  const hasSanitisation = Boolean(hasPhDoser || hasSaltChlorinator || hasLiquidChlorine);
  const hasHeating = Boolean(heating);

  // ===================== Sanitisation Edit (Independent from Pump) =====================
  type SanGroup = 'ph' | 'chlorine';
  type PhEditKey = 'ph_dosing_time' | 'desired_ph_level' | 'acid_drum_volume' | 'remain_acid_volume' | 'dose_intervals' | 'ph_dose_rate' | 'max_dose_pre_day';
  type ChlorineEditKey = 'desired_orp_level_mineral' | 'desired_orp_level_liquid' | 'chlorine_dosing_time' | 'liquid_dose_rate' | 'dose_intervals' | 'max_dose_pre_day' | 'chlorine_drum_volume' | 'remain_chlorine_volume';
  type SanEditMeta = { group: SanGroup; key: PhEditKey | ChlorineEditKey; label: string; unit?: string } | null;

  const [sanEditOpen, setSanEditOpen] = React.useState<boolean>(false);
  const [sanEditDraft, setSanEditDraft] = React.useState<string>('');
  const [sanEditMeta, setSanEditMeta] = React.useState<SanEditMeta>(null);
  const [sanEditError, setSanEditError] = React.useState<string>('');
  const [sanResetOpen, setSanResetOpen] = React.useState<{ open: boolean; group: SanGroup | null; label?: string; message?: string }>(
    { open: false, group: null, label: '', message: '' }
  );

  // Sanitisation-specific validation (independent)
  const validateSanValue = React.useCallback((raw: string, meta: SanEditMeta): string => {
    const value = raw.trim();
    if (!value) return 'Please enter a value';
    if (!meta) return 'Missing edit info';
    // desired_ph_level allows decimals (e.g. 7.5); others integers
    if (meta.key === 'desired_ph_level') {
      if (!/^-?\d+(?:\.\d+)?$/.test(value)) return 'Enter a valid decimal number';
      const n = Number(value);
      if (n < 0 || n > 14) return 'pH must be between 0 and 14';
      return '';
    }
    // Integers for other fields
    if (!/^-?\d+$/.test(value)) return 'Integers only (no decimals)';
    const n = Number(value);
    if (meta.key === 'chlorine_dosing_time' || meta.key === 'ph_dosing_time' || meta.key === 'dose_intervals') {
      if (n < 0) return 'Time must be zero or positive';
    }
    if (meta.key === 'acid_drum_volume' || meta.key === 'remain_acid_volume' || meta.key === 'chlorine_drum_volume' || meta.key === 'remain_chlorine_volume') {
      if (n <= 0) return 'Volume must be positive';
    }
    if (meta.key === 'liquid_dose_rate' || meta.key === 'ph_dose_rate') {
      if (n < 0) return 'Dose rate must be zero or positive';
    }
    if (meta.key === 'desired_orp_level_mineral' || meta.key === 'desired_orp_level_liquid') {
      if (n < 0) return 'ORP must be zero or positive';
    }
    if (meta.key === 'max_dose_pre_day') {
      if (n < 0) return 'Max dose per day must be zero or positive';
    }
    return '';
  }, []);

  const openSanEdit = React.useCallback((meta: NonNullable<SanEditMeta>, currentValue?: number | null) => {
    setSanEditMeta(meta);
    const draft = currentValue != null ? String(meta.key === 'desired_ph_level' ? (currentValue / 10).toFixed(2) : currentValue) : '';
    setSanEditDraft(draft);
    setSanEditError(validateSanValue(draft, meta));
    setSanEditOpen(true);
  }, [validateSanValue]);

  // Toggle handlers
  const togglePhDosing = React.useCallback(async () => {
    if (!selectedDeviceId || !ph) return;
    const old = Boolean(ph.ph_dosing);
    const next = !old;
    // Optimistic UI: update switch state immediately
    setData(prev => prev ? { ...prev, phSettings: { ...(prev.phSettings as PhSettings), ph_dosing: next } } : prev);
    const payload = {
      ph_dosing: next,
      ph_dosing_time: ph.ph_dosing_time ?? 0,
      desired_ph_level: ph.desired_ph_level ?? 0,
      acid_drum_volume: ph.acid_drum_volume ?? 0,
      remain_acid_volume: ph.remain_acid_volume ?? 0,
      reset_acid_volume: false,
      max_dose_pre_day: ph.max_dose_pre_day ?? 0,
      dose_intervals: ph.dose_intervals ?? 0,
      ph_dose_rate: ph.ph_dose_rate ?? 0,
    };
    dispatch(setLoading(true));
    try {
      await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setPhSettings`, payload);
      await fetchData(selectedDeviceId);
    } catch (err) {
      console.error('Failed to toggle pH dosing:', err);
      // Revert on failure
      setData(prev => prev ? { ...prev, phSettings: { ...(prev.phSettings as PhSettings), ph_dosing: old } } : prev);
    } finally {
      dispatch(setLoading(false));
    }
  }, [selectedDeviceId, ph, dispatch, fetchData]);

  const toggleOrpControlled = React.useCallback(async () => {
    if (!selectedDeviceId || !chlorine) return;
    const old = Boolean(chlorine.orp_controlled);
    const next = !old;
    // Optimistic UI: update switch state immediately
    setData(prev => prev ? { ...prev, chlorineSettings: { ...(prev.chlorineSettings as ChlorineSettings), orp_controlled: next } } : prev);
    const payload = {
      orp_controlled: next,
      desired_orp_level_mineral: chlorine.desired_orp_level_mineral ?? 0,
      liquid_chlorine_dosing: chlorine.liquid_chlorine_dosing ?? false,
      desired_orp_level_liquid: chlorine.desired_orp_level_liquid ?? 0,
      chlorine_dosing_time: chlorine.chlorine_dosing_time ?? 0,
      chlorine_drum_volume: chlorine.chlorine_drum_volume ?? 0,
      remain_chlorine_volume: chlorine.remain_chlorine_volume ?? 0,
      reset_chlorine_volume: false,
      max_dose_pre_day: chlorine.max_dose_pre_day ?? 0,
      dose_intervals: chlorine.dose_intervals ?? 0,
      liquid_dose_rate: chlorine.liquid_dose_rate ?? 0,
    };
    dispatch(setLoading(true));
    try {
      await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setChlorineSettings`, payload);
      await fetchData(selectedDeviceId);
    } catch (err) {
      console.error('Failed to toggle ORP controlled:', err);
      // Revert on failure
      setData(prev => prev ? { ...prev, chlorineSettings: { ...(prev.chlorineSettings as ChlorineSettings), orp_controlled: old } } : prev);
    } finally {
      dispatch(setLoading(false));
    }
  }, [selectedDeviceId, chlorine, dispatch, fetchData]);

  const toggleLiquidChlorineDosing = React.useCallback(async () => {
    if (!selectedDeviceId || !chlorine) return;
    const old = Boolean(chlorine.liquid_chlorine_dosing);
    const next = !old;
    // Optimistic UI: update switch state immediately
    setData(prev => prev ? { ...prev, chlorineSettings: { ...(prev.chlorineSettings as ChlorineSettings), liquid_chlorine_dosing: next } } : prev);
    const payload = {
      orp_controlled: chlorine.orp_controlled ?? false,
      desired_orp_level_mineral: chlorine.desired_orp_level_mineral ?? 0,
      liquid_chlorine_dosing: next,
      desired_orp_level_liquid: chlorine.desired_orp_level_liquid ?? 0,
      chlorine_dosing_time: chlorine.chlorine_dosing_time ?? 0,
      chlorine_drum_volume: chlorine.chlorine_drum_volume ?? 0,
      remain_chlorine_volume: chlorine.remain_chlorine_volume ?? 0,
      reset_chlorine_volume: false,
      max_dose_pre_day: chlorine.max_dose_pre_day ?? 0,
      dose_intervals: chlorine.dose_intervals ?? 0,
      liquid_dose_rate: chlorine.liquid_dose_rate ?? 0,
    };
    dispatch(setLoading(true));
    try {
      await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setChlorineSettings`, payload);
      await fetchData(selectedDeviceId);
    } catch (err) {
      console.error('Failed to toggle Liquid Chlorine dosing:', err);
      // Revert on failure
      setData(prev => prev ? { ...prev, chlorineSettings: { ...(prev.chlorineSettings as ChlorineSettings), liquid_chlorine_dosing: old } } : prev);
    } finally {
      dispatch(setLoading(false));
    }
  }, [selectedDeviceId, chlorine, dispatch, fetchData]);

  // Reset handlers (Confirm then send)
  const requestResetPhRemainVolume = React.useCallback(() => {
    setSanResetOpen({ open: true, group: 'ph', label: 'Reset Acid Volume', message: 'Do you want to reset remaining acid to drum volume?' });
  }, []);

  const requestResetChlorineRemainVolume = React.useCallback(() => {
    setSanResetOpen({ open: true, group: 'chlorine', label: 'Reset Chlorine Volume', message: 'Do you want to reset remaining chlorine to drum volume?' });
  }, []);

  const handleConfirmReset = React.useCallback(async () => {
    if (!selectedDeviceId || !sanResetOpen.group) { setSanResetOpen({ open: false, group: null }); return; }
    if (sanResetOpen.group === 'ph') {
      if (!ph) { setSanResetOpen({ open: false, group: null }); return; }
      const payload = {
        ph_dosing: ph.ph_dosing ?? false,
        ph_dosing_time: ph.ph_dosing_time ?? 0,
        desired_ph_level: ph.desired_ph_level ?? 0,
        acid_drum_volume: ph.acid_drum_volume ?? 0,
        remain_acid_volume: ph.acid_drum_volume ?? 0,
        reset_acid_volume: true,
        max_dose_pre_day: ph.max_dose_pre_day ?? 0,
        dose_intervals: ph.dose_intervals ?? 0,
        ph_dose_rate: ph.ph_dose_rate ?? 0,
      };
      dispatch(setLoading(true));
      try {
        await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setPhSettings`, payload);
        await fetchData(selectedDeviceId);
      } finally { dispatch(setLoading(false)); }
    } else if (sanResetOpen.group === 'chlorine') {
      if (!chlorine) { setSanResetOpen({ open: false, group: null }); return; }
      const payload = {
        orp_controlled: chlorine.orp_controlled ?? false,
        desired_orp_level_mineral: chlorine.desired_orp_level_mineral ?? 0,
        liquid_chlorine_dosing: chlorine.liquid_chlorine_dosing ?? false,
        desired_orp_level_liquid: chlorine.desired_orp_level_liquid ?? 0,
        chlorine_dosing_time: chlorine.chlorine_dosing_time ?? 0,
        chlorine_drum_volume: chlorine.chlorine_drum_volume ?? 0,
        remain_chlorine_volume: chlorine.chlorine_drum_volume ?? 0,
        reset_chlorine_volume: true,
        max_dose_pre_day: chlorine.max_dose_pre_day ?? 0,
        dose_intervals: chlorine.dose_intervals ?? 0,
        liquid_dose_rate: chlorine.liquid_dose_rate ?? 0,
      };
      dispatch(setLoading(true));
      try {
        await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setChlorineSettings`, payload);
        await fetchData(selectedDeviceId);
      } finally { dispatch(setLoading(false)); }
    }
    setSanResetOpen({ open: false, group: null });
  }, [selectedDeviceId, sanResetOpen.group, ph, chlorine, dispatch, fetchData]);

  // Submit sanitisation edit value
  const submitSanitisationSetting = React.useCallback(async () => {
    if (!selectedDeviceId || !sanEditMeta) { setSanEditError('Missing device or edit item info'); return; }
    const err = validateSanValue(sanEditDraft, sanEditMeta);
    if (err) { setSanEditError(err); return; }

    // parse and convert
    let parsed = Number(sanEditDraft);
    let payload: Record<string, any> = {};
    if (sanEditMeta.group === 'ph') {
      if (!ph) { console.warn('pH settings not loaded; aborting update to avoid zeroing fields.'); return; }
      switch (sanEditMeta.key as PhEditKey) {
        case 'desired_ph_level':
          // convert to int by *10
          parsed = Math.round(parsed * 10);
          payload = {
            ph_dosing: ph.ph_dosing ?? false,
            ph_dosing_time: ph.ph_dosing_time ?? 0,
            desired_ph_level: parsed,
            acid_drum_volume: ph.acid_drum_volume ?? 0,
            remain_acid_volume: ph.remain_acid_volume ?? 0,
            reset_acid_volume: false,
            max_dose_pre_day: ph.max_dose_pre_day ?? 0,
            dose_intervals: ph.dose_intervals ?? 0,
            ph_dose_rate: ph.ph_dose_rate ?? 0,
          };
          break;
        case 'acid_drum_volume':
          // liters to mL
          parsed = parsed * 1000;
          payload = {
            ph_dosing: ph.ph_dosing ?? false,
            ph_dosing_time: ph.ph_dosing_time ?? 0,
            desired_ph_level: ph.desired_ph_level ?? 0,
            acid_drum_volume: parsed,
            remain_acid_volume: ph.remain_acid_volume ?? 0,
            reset_acid_volume: false,
            max_dose_pre_day: ph.max_dose_pre_day ?? 0,
            dose_intervals: ph.dose_intervals ?? 0,
            ph_dose_rate: ph.ph_dose_rate ?? 0,
          };
          break;
        case 'remain_acid_volume':
          // liters to mL
          parsed = parsed * 1000;
          payload = {
            ph_dosing: ph.ph_dosing ?? false,
            ph_dosing_time: ph.ph_dosing_time ?? 0,
            desired_ph_level: ph.desired_ph_level ?? 0,
            acid_drum_volume: ph.acid_drum_volume ?? 0,
            remain_acid_volume: parsed,
            reset_acid_volume: false,
            max_dose_pre_day: ph.max_dose_pre_day ?? 0,
            dose_intervals: ph.dose_intervals ?? 0,
            ph_dose_rate: ph.ph_dose_rate ?? 0,
          };
          break;
        case 'ph_dosing_time':
        case 'dose_intervals':
        case 'ph_dose_rate':
        case 'max_dose_pre_day':
          payload = {
            ph_dosing: ph.ph_dosing ?? false,
            ph_dosing_time: sanEditMeta.key === 'ph_dosing_time' ? parsed : (ph.ph_dosing_time ?? 0),
            desired_ph_level: ph.desired_ph_level ?? 0,
            acid_drum_volume: ph.acid_drum_volume ?? 0,
            remain_acid_volume: ph.remain_acid_volume ?? 0,
            reset_acid_volume: false,
            max_dose_pre_day: sanEditMeta.key === 'max_dose_pre_day' ? parsed : (ph.max_dose_pre_day ?? 0),
            dose_intervals: sanEditMeta.key === 'dose_intervals' ? parsed : (ph.dose_intervals ?? 0),
            ph_dose_rate: sanEditMeta.key === 'ph_dose_rate' ? parsed : (ph.ph_dose_rate ?? 0),
          };
          break;
      }
      dispatch(setLoading(true));
      try {
        await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setPhSettings`, payload);
        await fetchData(selectedDeviceId);
      } finally { dispatch(setLoading(false)); }
    } else if (sanEditMeta.group === 'chlorine') {
      if (!chlorine) { console.warn('Chlorine settings not loaded; aborting update to avoid zeroing fields.'); return; }
      switch (sanEditMeta.key as ChlorineEditKey) {
        case 'chlorine_drum_volume':
          // liters to mL
          parsed = parsed * 1000;
          payload = {
            orp_controlled: chlorine.orp_controlled ?? false,
            desired_orp_level_mineral: chlorine.desired_orp_level_mineral ?? 0,
            liquid_chlorine_dosing: chlorine.liquid_chlorine_dosing ?? false,
            desired_orp_level_liquid: chlorine.desired_orp_level_liquid ?? 0,
            chlorine_dosing_time: chlorine.chlorine_dosing_time ?? 0,
            chlorine_drum_volume: parsed,
            remain_chlorine_volume: chlorine.remain_chlorine_volume ?? 0,
            reset_chlorine_volume: false,
            max_dose_pre_day: chlorine.max_dose_pre_day ?? 0,
            dose_intervals: chlorine.dose_intervals ?? 0,
            liquid_dose_rate: chlorine.liquid_dose_rate ?? 0,
          };
          break;
        case 'remain_chlorine_volume':
          // liters to mL
          parsed = parsed * 1000;
          payload = {
            orp_controlled: chlorine.orp_controlled ?? false,
            desired_orp_level_mineral: chlorine.desired_orp_level_mineral ?? 0,
            liquid_chlorine_dosing: chlorine.liquid_chlorine_dosing ?? false,
            desired_orp_level_liquid: chlorine.desired_orp_level_liquid ?? 0,
            chlorine_dosing_time: chlorine.chlorine_dosing_time ?? 0,
            chlorine_drum_volume: chlorine.chlorine_drum_volume ?? 0,
            remain_chlorine_volume: parsed,
            reset_chlorine_volume: false,
            max_dose_pre_day: chlorine.max_dose_pre_day ?? 0,
            dose_intervals: chlorine.dose_intervals ?? 0,
            liquid_dose_rate: chlorine.liquid_dose_rate ?? 0,
          };
          break;
        case 'desired_orp_level_mineral':
        case 'desired_orp_level_liquid':
        case 'chlorine_dosing_time':
        case 'liquid_dose_rate':
        case 'dose_intervals':
        case 'max_dose_pre_day':
          payload = {
            orp_controlled: chlorine.orp_controlled ?? false,
            desired_orp_level_mineral: sanEditMeta.key === 'desired_orp_level_mineral' ? parsed : (chlorine.desired_orp_level_mineral ?? 0),
            liquid_chlorine_dosing: chlorine.liquid_chlorine_dosing ?? false,
            desired_orp_level_liquid: sanEditMeta.key === 'desired_orp_level_liquid' ? parsed : (chlorine.desired_orp_level_liquid ?? 0),
            chlorine_dosing_time: sanEditMeta.key === 'chlorine_dosing_time' ? parsed : (chlorine.chlorine_dosing_time ?? 0),
            chlorine_drum_volume: chlorine.chlorine_drum_volume ?? 0,
            remain_chlorine_volume: chlorine.remain_chlorine_volume ?? 0,
            reset_chlorine_volume: false,
            max_dose_pre_day: sanEditMeta.key === 'max_dose_pre_day' ? parsed : (chlorine.max_dose_pre_day ?? 0),
            dose_intervals: sanEditMeta.key === 'dose_intervals' ? parsed : (chlorine.dose_intervals ?? 0),
            liquid_dose_rate: sanEditMeta.key === 'liquid_dose_rate' ? parsed : (chlorine.liquid_dose_rate ?? 0),
          };
          break;
      }
      dispatch(setLoading(true));
      try {
        await apiService.post<{ message: string }>(`/api/v1/iot-shadow/device/${selectedDeviceId}/setChlorineSettings`, payload);
        await fetchData(selectedDeviceId);
      } finally { dispatch(setLoading(false)); }
    }
    setSanEditOpen(false);
  }, [selectedDeviceId, sanEditMeta, sanEditDraft, validateSanValue, ph, chlorine, dispatch, fetchData]);

  const openEdit = useCallback((meta: { key: PumpEditKey; label: string; unit: string }, currentValue?: number | null) => {
    setEditMeta(meta);
    setEditDraft(currentValue != null ? String(currentValue) : '');
    setEditError(validateValue(currentValue != null ? String(currentValue) : '', meta));
    setEditOpen(true);
  }, [validateValue]);

  // Submit changes to backend
  const submitPumpSetting = useCallback(async (deviceId: string, key: PumpEditKey, value: number) => {
    if (generalPumpSettingKeys.has(key)) {
      if (!pumpSettings) {
        console.warn('General pump settings not loaded; aborting update to avoid zeroing fields.');
        return;
      }

      const payload: Record<string, number> = {
        backwash_pressure: pumpSettings?.backwash_pressure ?? 150,
        no_flow_cutoff_time: pumpSettings?.no_flow_cutoff_time ?? pump?.no_flow_cutoff_time ?? 60,
        no_flow_retry_times: pumpSettings?.no_flow_retry_times ?? 3,
        minimum_pressure_cutoff: pumpSettings?.minimum_pressure_cutoff ?? 30,
        minimum_flow_cutoff: pumpSettings?.minimum_flow_cutoff ?? 30,
        drycheckDisable: dryRunRemainingSeconds > 0 ? DRY_RUN_DISABLE_COMMAND : DRY_RUN_ENABLE_COMMAND,
      };
      payload[key] = value;

      dispatch(setLoading(true));
      try {
        await apiService.post<{ message: string }>(
          `/api/v1/iot-shadow/device/${deviceId}/setPumpSettings`,
          payload
        );
      } finally {
        dispatch(setLoading(false));
      }
      return;
    }

    // For other keys: include existing Filter Pump settings to avoid zeroing missing fields
    if (!pump) {
      console.warn('Filter pump settings not loaded; aborting update to avoid zeroing fields.');
      return;
    }

    const payload: Record<string, number> = {
      no_flow_cutoff_time: pump?.no_flow_cutoff_time ?? 0,
      // Note: request field is no_flow_retry_times (not "try")
      no_flow_retry_times: pump?.no_flow_try_times ?? 0,
      max_run_current: pump?.max_run_current ?? 0,
      pump_prime_time_100: pump?.pump_prime_time_100 ?? 0,
      pump_start_speed: pump?.pump_start_speed ?? 0,
      min_allow_pump_speed: pump?.min_allow_pump_speed ?? 0,
      heater_pump_speed: pump?.heater_pump_speed ?? 0,
      solar_heating_pump_speed: pump?.solar_heating_pump_speed ?? 0,
      cleaner_pump_speed: pump?.cleaner_pump_speed ?? 0,
      spa_mode_pump_speed: pump?.spa_mode_pump_speed ?? 0,
      drycheckDisable: pump?.drycheck_disable ?? 0,
    };

    // Replace the edited key with new value
    payload[key] = value;
    dispatch(setLoading(true));
    try {
      await apiService.post<{ message: string }>(
        `/api/v1/iot-shadow/device/${deviceId}/setFilterPumpSettings`,
        payload
      );
    } finally {
      dispatch(setLoading(false));
    }
  }, [pump, pumpSettings, dryRunRemainingSeconds, generalPumpSettingKeys, dispatch]);

  const handleConfirmEdit = useCallback(async () => {
    if (!selectedDeviceId || !editMeta) {
      setEditError('Missing device or edit item info');
      return;
    }
    // Validate: integers only; percentage keys must be 0–100
    const err = validateValue(editDraft, editMeta);
    if (err) {
      setEditError(err);
      return;
    }
    const parsed = Number(editDraft);
    try {
      await submitPumpSetting(selectedDeviceId, editMeta.key, parsed);
      // 成功后可选择刷新数据
      if (selectedDeviceId) await fetchData(selectedDeviceId);
    } finally {
      setEditOpen(false);
    }
  }, [selectedDeviceId, editMeta, editDraft, submitPumpSetting, validateValue, fetchData]);

  const toggleDryRunProtection = useCallback(async () => {
    if (!selectedDeviceId) return;

    // Use the currently rendered countdown as the source of truth.
    // `filterPumpSettings.drycheck_disable` can lag behind `pumpInfo`.
    const previousRawValue = pumpSettings?.drycheck_disable ?? pump?.drycheck_disable ?? dryRunRemainingSeconds;
    const previousNormalized = dryRunRemainingSeconds;
    const nextCommand = previousNormalized === 0 ? DRY_RUN_DISABLE_COMMAND : DRY_RUN_ENABLE_COMMAND;
    const nextCountdown = nextCommand === DRY_RUN_DISABLE_COMMAND ? DRY_RUN_DISABLE_TOTAL_SECONDS : 0;
    const isEnablingProtection = nextCommand === DRY_RUN_ENABLE_COMMAND;

    if (isEnablingProtection) {
      setIsDryRunTogglePending(true);
      dispatch(setLoading(true));
    }

    setDryRunRemainingSeconds(nextCountdown);
    setData((prev) => prev ? {
      ...prev,
      pumpSettings: {
        ...prev.pumpSettings,
        drycheck_disable: nextCommand,
      },
      filterPumpSettings: {
        ...prev.filterPumpSettings,
        drycheck_disable: nextCountdown,
      },
    } : prev);

    const payload = {
      backwash_pressure: pumpSettings?.backwash_pressure ?? 150,
      no_flow_cutoff_time: pumpSettings?.no_flow_cutoff_time ?? pump?.no_flow_cutoff_time ?? 60,
      no_flow_retry_times: pumpSettings?.no_flow_retry_times ?? 3,
      minimum_pressure_cutoff: pumpSettings?.minimum_pressure_cutoff ?? 30,
      minimum_flow_cutoff: pumpSettings?.minimum_flow_cutoff ?? 30,
      drycheckDisable: nextCommand,
      drycheck_disable: nextCommand,
    };

    try {
      await apiService.post<{ message: string }>(
        `/api/v1/iot-shadow/device/${selectedDeviceId}/setPumpSettings`,
        payload
      );
      await fetchData(selectedDeviceId, { silent: true });
    } catch (err) {
      console.error('Failed to toggle dry run protection:', err);
      setDryRunRemainingSeconds(previousNormalized);
      setData((prev) => prev ? {
        ...prev,
        pumpSettings: {
          ...prev.pumpSettings,
          drycheck_disable: previousRawValue,
        },
        filterPumpSettings: {
          ...prev.filterPumpSettings,
          drycheck_disable: previousRawValue,
        },
      } : prev);
    } finally {
      if (isEnablingProtection) {
        setIsDryRunTogglePending(false);
        dispatch(setLoading(false));
      }
    }
  }, [selectedDeviceId, pump, pumpSettings, fetchData, dryRunRemainingSeconds, dispatch]);

  return (
    <div className="space-y-4">
      {/* 页头：左侧标题，右侧设备选择器（参考 DeviceSetupPage） */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight">Device Settings</h2>
        </div>
        <div className="flex items-center">
          <DeviceSelector onDeviceChange={handleDeviceChange} selectedDeviceId={selectedDeviceId || ''} />
        </div>
      </div>

      {/* Filter Pump Section */}
      <Card>
        <div className="p-4 space-y-4">
          <SectionTitle title="Filter Pump" />
          {/* General 部分 */}
          <div>
            <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1">General</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <EditableRow
                  label="Backwash Pressure"
                  value={formatKpa(pumpSettings?.backwash_pressure)}
                  onEdit={() => openEdit({ key: 'backwash_pressure', label: 'Backwash Pressure', unit: 'kpa' }, pumpSettings?.backwash_pressure)}
                />
                <EditableRow
                  label="Pump Prime Time"
                  value={formatSeconds(pumpSettings?.no_flow_cutoff_time ?? pump?.no_flow_cutoff_time ?? 60)}
                  onEdit={() => openEdit({ key: 'no_flow_cutoff_time', label: 'Pump Prime Time', unit: 'seconds' }, pumpSettings?.no_flow_cutoff_time ?? pump?.no_flow_cutoff_time ?? 60)}
                />
                <EditableRow
                  label="No Flow & Pressure Retry Times"
                  value={pumpSettings?.no_flow_retry_times ?? 3}
                  onEdit={() => openEdit({ key: 'no_flow_retry_times', label: 'No Flow & Pressure Retry Times', unit: '' }, pumpSettings?.no_flow_retry_times ?? 3)}
                />
              </div>
              <div>
                <EditableRow
                  label="Minimum Pressure Cutoff"
                  value={formatKpa(pumpSettings?.minimum_pressure_cutoff ?? 30)}
                  onEdit={() => openEdit({ key: 'minimum_pressure_cutoff', label: 'Minimum Pressure Cutoff', unit: 'kPa' }, pumpSettings?.minimum_pressure_cutoff ?? 30)}
                />
                <EditableRow
                  label="Minimum Flow Cutoff"
                  value={`${pumpSettings?.minimum_flow_cutoff ?? 30} LPM`}
                  onEdit={() => openEdit({ key: 'minimum_flow_cutoff', label: 'Minimum Flow Cutoff', unit: 'LPM' }, pumpSettings?.minimum_flow_cutoff ?? 30)}
                />
              </div>
            </div>
          </div>

          {/* VSD 部分（根据泵类型决定是否显示），跳过 No‑Flow 项 */}
          {isVsd && (
            <div>
              <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1">VSD (Variable Speed Drive)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  {isSplashMeVsdPump && (
                    <EditableRow label="Max Run Current" value={formatAmp(pump?.max_run_current)} onEdit={() => openEdit({ key: 'max_run_current', label: 'Max Run Current', unit: 'A' }, pump?.max_run_current)} />
                  )}
                  {isSplashMeVsdPump && (
                    <EditableRow label="Start Speed" value={formatPercent(pump?.pump_start_speed)} onEdit={() => openEdit({ key: 'pump_start_speed', label: 'Start Speed', unit: '%' }, pump?.pump_start_speed)} />
                  )}
                  <EditableRow label="Min Allow Pump Speed" value={formatPercent(pump?.min_allow_pump_speed)} onEdit={() => openEdit({ key: 'min_allow_pump_speed', label: 'Min Allow Pump Speed', unit: '%' }, pump?.min_allow_pump_speed)} />
                </div>
                <div>{/* reserved */}</div>
              </div>
            </div>
          )}

          {(hasSpa || hasHeater || hasSolar || hasCleaner) && (
            <div>
              <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1">Pump Speed</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  {hasSpa && (
                    <EditableRow label="Spa Mode Pump Speed" value={formatPercent(pump?.spa_mode_pump_speed)} onEdit={() => openEdit({ key: 'spa_mode_pump_speed', label: 'Spa Mode Pump Speed', unit: '%' }, pump?.spa_mode_pump_speed)} />
                  )}
                  {hasHeater && (
                    <EditableRow label="Heater Pump Speed" value={formatPercent(pump?.heater_pump_speed)} onEdit={() => openEdit({ key: 'heater_pump_speed', label: 'Heater Pump Speed', unit: '%' }, pump?.heater_pump_speed)} />
                  )}
                </div>
                <div>
                  {hasSolar && (
                    <EditableRow label="Solar Heating Pump Speed" value={formatPercent(pump?.solar_heating_pump_speed)} onEdit={() => openEdit({ key: 'solar_heating_pump_speed', label: 'Solar Heating Pump Speed', unit: '%' }, pump?.solar_heating_pump_speed)} />
                  )}
                  {hasCleaner && (
                    <EditableRow label="Cleaner Pump Speed" value={formatPercent(pump?.cleaner_pump_speed)} onEdit={() => openEdit({ key: 'cleaner_pump_speed', label: 'Cleaner Pump Speed', unit: '%' }, pump?.cleaner_pump_speed)} />
                  )}
                </div>
              </div>
            </div>
          )}

          {isDryRunSupported && (
            <div>
              <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1">Dry Run</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-1">
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Pump Dry Run Protection</div>
                    </div>
                    <Switch checked={dryRunProtectionEnabled} onChange={toggleDryRunProtection} />
                  </div>

                  {dryRunRemainingSeconds > 0 && (
                    <div className="rounded-lg border border-sky-200 bg-sky-50/80 px-4 py-3 dark:border-sky-900/40 dark:bg-sky-950/20">
                      <div className="mb-2 flex items-center justify-between text-sm">
                        <span className="font-medium text-sky-700 dark:text-sky-300">Temporarily Disabled for</span>
                        <span className="font-semibold text-sky-700 dark:text-sky-300">
                          {formatCountdown(dryRunRemainingSeconds)}
                        </span>
                      </div>
                      <LinearProgress
                        variant="determinate"
                        value={(dryRunRemainingSeconds / DRY_RUN_DISABLE_TOTAL_SECONDS) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 999,
                          backgroundColor: 'rgba(14, 165, 233, 0.15)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 999,
                            backgroundColor: '#0ea5e9',
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
      {/* 编辑弹窗（Filter Pump） */}
      <ConfirmDialog
        open={editOpen}
        title={`Edit ${editMeta?.label ?? ''}`}
        onCancel={() => setEditOpen(false)}
        onOk={handleConfirmEdit}
        cancelText="Cancel"
        okText="Save"
        fullWidth
        maxWidth="sm"
      >
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={`${editMeta?.label ?? ''}${editMeta?.unit ? ` (${editMeta.unit})` : ''}`}
          type="number"
          value={editDraft}
          onChange={(e) => {
            const v = e.target.value;
            setEditDraft(v);
            setEditError(validateValue(v, editMeta));
          }}
          error={Boolean(editError)}
          helperText={editError}
          InputLabelProps={{ className: '!text-gray-700 dark:!text-gray-300' }}
          FormHelperTextProps={{ className: '!text-red-600 dark:!text-red-400' }}
          inputProps={{ step: 1, inputMode: 'numeric', pattern: '[0-9]*', className: '!text-gray-900 dark:!text-gray-100' }}
          className="dark:[&_.MuiOutlinedInput-notchedOutline]:!border-white dark:[&_.MuiOutlinedInput-root:hover_.MuiOutlinedInput-notchedOutline]:!border-white dark:[&_.MuiOutlinedInput-root.Mui-focused_.MuiOutlinedInput-notchedOutline]:!border-white"
        />
      </ConfirmDialog>

      {/* Sanitisation Section */}
      <Card>
        <div className="p-4 space-y-3">
          <SectionTitle title="Sanitisation" />
          {hasSanitisation ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* pH Settings - Only show if pH doser is present */}
              {hasPhDoser && phSettingIndexList.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1 mb-2">pH Settings</h4>
                  {/* Toggle for pH dosing */}
                  {phSettingIndexList.includes(0) && (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-gray-500 dark:text-gray-400">pH Dosing</span>
                      <Switch checked={Boolean(ph?.ph_dosing)} onChange={togglePhDosing} />
                    </div>
                  )}
                  {/* Editable rows (independent UI components) */}
                  {phSettingIndexList.includes(1) && (
                    <div className="flex items-center justify-between py-1 gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">pH Dosing Time</span>
                      <div className="inline-flex items-center gap-2 shrink-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatSeconds(ph?.ph_dosing_time)}</span>
                        <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'ph', key: 'ph_dosing_time', label: 'pH Dosing Time', unit: 'seconds' }, ph?.ph_dosing_time)} aria-label="Edit pH Dosing Time">
                          <SquarePen className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {phSettingIndexList.includes(2) && (
                    <div className="flex items-center justify-between py-1 gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Desired pH Level</span>
                      <div className="inline-flex items-center gap-2 shrink-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatPh(ph?.desired_ph_level)}</span>
                        <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'ph', key: 'desired_ph_level', label: 'Desired pH Level' }, ph?.desired_ph_level)} aria-label="Edit Desired pH Level">
                          <SquarePen className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {phSettingIndexList.includes(3) && (
                    <div className="flex items-center justify-between py-1 gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Acid Drum Volume</span>
                      <div className="inline-flex items-center gap-2 shrink-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatLiters(ph?.acid_drum_volume)}</span>
                        <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'ph', key: 'acid_drum_volume', label: 'Acid Drum Volume', unit: 'L' }, ph?.acid_drum_volume ? Math.round(ph.acid_drum_volume / 1000) : undefined)} aria-label="Edit Acid Drum Volume">
                          <SquarePen className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {phSettingIndexList.includes(4) && (
                    <div className="py-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Remain Acid Volume</span>
                        <div className="inline-flex items-center gap-2 shrink-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatLiters(ph?.remain_acid_volume)}</span>
                          {/* Edit remain acid volume (in liters) */}
                          <button
                            type="button"
                            className="inline-flex items-center text-primary-600 hover:text-primary-700"
                            onClick={() => openSanEdit(
                              { group: 'ph', key: 'remain_acid_volume', label: 'Remain Acid Volume', unit: 'L' },
                              ph?.remain_acid_volume ? Math.round(ph.remain_acid_volume / 1000) : undefined
                            )}
                            aria-label="Edit Remain Acid Volume"
                          >
                            <SquarePen className="h-4 w-4" />
                          </button>
                          {/* Reset button next to edit */}
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
                            onClick={requestResetPhRemainVolume}
                            aria-label="Reset Acid Volume"
                          >
                           <FiRotateCcw className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {/* Percentage remaining text below, following Android safeDivision logic */}
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                        {`${(safeDivision(ph?.remain_acid_volume, ph?.acid_drum_volume) * 100).toFixed(2)}% acid remaining`}
                      </div>
                    </div>
                  )}
                  {phSettingIndexList.includes(6) && (
                    <div className="py-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Max Dose Per Day</span>
                        <div className="inline-flex items-center gap-2 shrink-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatTimes(ph?.max_dose_pre_day)}</span>
                          <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'ph', key: 'max_dose_pre_day', label: 'Max Dose Per Day' }, ph?.max_dose_pre_day)} aria-label="Edit Max Dose Per Day">
                            <SquarePen className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                        {`Today, SplashMe has dosed ${ph?.current_dose_today ?? 0} times`}
                      </div>
                    </div>
                  )}
                  {phSettingIndexList.includes(8) && (
                    <div className="flex items-center justify-between py-1 gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Dose Intervals</span>
                      <div className="inline-flex items-center gap-2 shrink-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatMinutes(ph?.dose_intervals)}</span>
                        <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'ph', key: 'dose_intervals', label: 'Dose Intervals', unit: 'minutes' }, ph?.dose_intervals)} aria-label="Edit Dose Intervals">
                          <SquarePen className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {phSettingIndexList.includes(9) && (
                    <div className="flex items-center justify-between py-1 gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">pH Dose Rate</span>
                      <div className="inline-flex items-center gap-2 shrink-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatMlPerMin(ph?.ph_dose_rate)}</span>
                        <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'ph', key: 'ph_dose_rate', label: 'pH Dose Rate', unit: 'mL/min' }, ph?.ph_dose_rate)} aria-label="Edit pH Dose Rate">
                          <SquarePen className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* ORP/Chlorine Settings - Show based on device presence */}
              {(hasSaltChlorinator || hasLiquidChlorine) && chlorineSettingIndexList.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-1 mb-2">Chlorine Settings</h4>
                  {/* Mineral Chlorinator Settings */}
                  {hasSaltChlorinator && (
                    <>
                      {chlorineSettingIndexList.includes(0) && (
                        <div className="flex items-center justify-between py-1">
                          <span className="text-sm text-gray-500 dark:text-gray-400">ORP Controlled</span>
                          <Switch checked={Boolean(chlorine?.orp_controlled)} onChange={toggleOrpControlled} />
                        </div>
                      )}
                      {chlorineSettingIndexList.includes(1) && (
                        <div className="flex items-center justify-between py-1 gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Desired ORP (Mineral)</span>
                          <div className="inline-flex items-center gap-2 shrink-0">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{chlorine?.desired_orp_level_mineral ?? '-'}</span>
                            <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'chlorine', key: 'desired_orp_level_mineral', label: 'Desired ORP (Mineral)' }, chlorine?.desired_orp_level_mineral)} aria-label="Edit Desired ORP (Mineral)">
                              <SquarePen className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Liquid Chlorine Settings */}
                  {hasLiquidChlorine && (
                    <>
                      {chlorineSettingIndexList.includes(2) && (
                        <div className="flex items-center justify-between py-1">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Liquid Chlorine Dosing</span>
                          <Switch checked={Boolean(chlorine?.liquid_chlorine_dosing)} onChange={toggleLiquidChlorineDosing} />
                        </div>
                      )}
                      {chlorineSettingIndexList.includes(3) && (
                        <div className="flex items-center justify-between py-1 gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Desired ORP (Liquid)</span>
                          <div className="inline-flex items-center gap-2 shrink-0">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{chlorine?.desired_orp_level_liquid ?? '-'}</span>
                            <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'chlorine', key: 'desired_orp_level_liquid', label: 'Desired ORP (Liquid)' }, chlorine?.desired_orp_level_liquid)} aria-label="Edit Desired ORP (Liquid)">
                              <SquarePen className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      {chlorineSettingIndexList.includes(4) && (
                        <div className="flex items-center justify-between py-1 gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Chlorine Dose Time</span>
                          <div className="inline-flex items-center gap-2 shrink-0">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatSeconds(chlorine?.chlorine_dosing_time)}</span>
                            <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'chlorine', key: 'chlorine_dosing_time', label: 'Chlorine Dose Time', unit: 'seconds' }, chlorine?.chlorine_dosing_time)} aria-label="Edit Chlorine Dose Time">
                              <SquarePen className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      {chlorineSettingIndexList.includes(5) && (
                        <div className="flex items-center justify-between py-1 gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Chlorine Dose Rate</span>
                          <div className="inline-flex items-center gap-2 shrink-0">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatMlPerMin(chlorine?.liquid_dose_rate)}</span>
                            <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'chlorine', key: 'liquid_dose_rate', label: 'Chlorine Dose Rate', unit: 'mL/min' }, chlorine?.liquid_dose_rate)} aria-label="Edit Chlorine Dose Rate">
                              <SquarePen className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      {chlorineSettingIndexList.includes(6) && (
                        <div className="flex items-center justify-between py-1 gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Chlorine Drum Volume</span>
                          <div className="inline-flex items-center gap-2 shrink-0">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatLiters(chlorine?.chlorine_drum_volume)}</span>
                            <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'chlorine', key: 'chlorine_drum_volume', label: 'Chlorine Drum Volume', unit: 'L' }, chlorine?.chlorine_drum_volume ? Math.round(chlorine.chlorine_drum_volume / 1000) : undefined)} aria-label="Edit Chlorine Drum Volume">
                              <SquarePen className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                      {chlorineSettingIndexList.includes(7) && (
                        <div className="py-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Remaining Chlorine Volume</span>
                            <div className="inline-flex items-center gap-2 shrink-0">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatLiters(chlorine?.remain_chlorine_volume)}</span>
                              <button
                                type="button"
                                className="inline-flex items-center text-primary-600 hover:text-primary-700"
                                onClick={() => openSanEdit(
                                  { group: 'chlorine', key: 'remain_chlorine_volume', label: 'Remaining Chlorine Volume', unit: 'L' },
                                  chlorine?.remain_chlorine_volume != null ? Math.round((chlorine.remain_chlorine_volume ?? 0) / 1000) : undefined
                                )}
                                aria-label="Edit Remaining Chlorine Volume"
                              >
                                <SquarePen className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
                                onClick={requestResetChlorineRemainVolume}
                                aria-label="Reset Remaining Chlorine Volume"
                              >
                                <FiRotateCcw className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                            {(() => {
                              const drumL = safeDivision(chlorine?.chlorine_drum_volume, 1000);
                              const remainingL = safeDivision(chlorine?.remain_chlorine_volume, 1000);
                              const pct = safeDivision(remainingL, drumL) * 100;
                              return `${pct.toFixed(2)}% chlorine remaining`;
                            })()}
                          </div>
                        </div>
                      )}
                      {chlorineSettingIndexList.includes(8) && (
                        <div className="py-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Max Dose Per Day</span>
                            <div className="inline-flex items-center gap-2 shrink-0">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatTimes(chlorine?.max_dose_pre_day)}</span>
                              <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openSanEdit({ group: 'chlorine', key: 'max_dose_pre_day', label: 'Max Dose Per Day' }, chlorine?.max_dose_pre_day)} aria-label="Edit Max Dose Per Day">
                                <SquarePen className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                            Today, SplashMe has dosed {chlorine?.current_dose_today ?? 0} times
                          </div>
                        </div>
                      )}
                      {false && chlorineSettingIndexList.includes(9) && (
                        <div className="flex items-center justify-between py-1">
                          <span className="text-sm text-gray-500 dark:text-gray-400">Current Dose Today</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatTimes(chlorine?.current_dose_today)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">No sanitisation devices detected.</div>
          )}
        </div>
      </Card>

      {/* Sanitisation Edit Dialog (Independent) */}
      <ConfirmDialog
        open={sanEditOpen}
        title={`Edit ${sanEditMeta?.label ?? ''}${sanEditMeta?.unit ? ` (${sanEditMeta.unit})` : ''}`}
        onCancel={() => setSanEditOpen(false)}
        onOk={submitSanitisationSetting}
        cancelText="Cancel"
        okText="Save"
        fullWidth
        maxWidth="sm"
      >
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={`${sanEditMeta?.label ?? ''}${sanEditMeta?.unit ? ` (${sanEditMeta.unit})` : ''}`}
          type={sanEditMeta?.key === 'desired_ph_level' ? 'text' : 'number'}
          value={sanEditDraft}
          onChange={(e) => { const v = e.target.value; setSanEditDraft(v); if (sanEditMeta) setSanEditError(validateSanValue(v, sanEditMeta)); }}
          error={Boolean(sanEditError)}
          helperText={sanEditError}
          InputLabelProps={{ className: '!text-gray-700 dark:!text-gray-300' }}
          FormHelperTextProps={{ className: '!text-red-600 dark:!text-red-400' }}
          inputProps={{ step: 1, inputMode: sanEditMeta?.key === 'desired_ph_level' ? 'decimal' : 'numeric', pattern: sanEditMeta?.key === 'desired_ph_level' ? '^-?\\d+(?:\\.\\d+)?$' : '[0-9]*', className: '!text-gray-900 dark:!text-gray-100' }}
          className="dark:[&_.MuiOutlinedInput-notchedOutline]:!border-white dark:[&_.MuiOutlinedInput-root:hover_.MuiOutlinedInput-notchedOutline]:!border-white dark:[&_.MuiOutlinedInput-root.Mui-focused_.MuiOutlinedInput-notchedOutline]:!border-white"
        />
      </ConfirmDialog>

      {/* Sanitisation Reset Confirm Dialog */}
      <ConfirmDialog
        open={sanResetOpen.open}
        title={sanResetOpen.label ?? ''}
        onCancel={() => setSanResetOpen({ open: false, group: null })}
        onOk={handleConfirmReset}
        cancelText="Cancel"
        okText="OK"
        fullWidth
        maxWidth="xs"
      >
        <div className="text-sm text-gray-700 dark:text-gray-200">{sanResetOpen.message}</div>
      </ConfirmDialog>

      {/* Heating Section (独立结构) */}
      <Card>
        <div className="p-4 space-y-3">
          <SectionTitle title="Heating" />
          {hasHeating ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                {/* Heater Cooldown */}
                <div className="flex items-center justify-between py-1 gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Heater Cooldown</span>
                  <div className="inline-flex items-center gap-2 shrink-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatSeconds(heating?.heater_cooldown)}</span>
                    <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openHeatEdit({ key: 'heater_cooldown', label: 'Heater Cooldown', unit: 'seconds' }, heating?.heater_cooldown)} aria-label="Edit Heater Cooldown">
                      <SquarePen className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* Spa Heater Cooldown */}
                <div className="flex items-center justify-between py-1 gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Spa Heater Cooldown</span>
                  <div className="inline-flex items-center gap-2 shrink-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatSeconds(heating?.spa_heater_cooldown)}</span>
                    <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openHeatEdit({ key: 'spa_heater_cooldown', label: 'Spa Heater Cooldown', unit: 'seconds' }, heating?.spa_heater_cooldown)} aria-label="Edit Spa Heater Cooldown">
                      <SquarePen className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* Solar Hysteresis Temp */}
                <div className="flex items-center justify-between py-1 gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Solar Hysteresis Temp</span>
                  <div className="inline-flex items-center gap-2 shrink-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatDegrees(heating?.solar_hysteresis_temp)}</span>
                    <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openHeatEdit({ key: 'solar_hysteresis_temp', label: 'Solar Hysteresis Temprature', unit: 'degrees' }, heating?.solar_hysteresis_temp)} aria-label="Edit Solar Hysteresis Temp">
                      <SquarePen className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* Solar Hysteresis Time */}
                <div className="flex items-center justify-between py-1 gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Solar Hysteresis Time</span>
                  <div className="inline-flex items-center gap-2 shrink-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{formatSeconds(heating?.solar_hysteresis_time)}</span>
                    <button type="button" className="inline-flex items-center text-primary-600 hover:text-primary-700" onClick={() => openHeatEdit({ key: 'solar_hysteresis_time', label: 'Solar Hysteresis Time', unit: 'seconds' }, heating?.solar_hysteresis_time)} aria-label="Edit Solar Hysteresis Time">
                      <SquarePen className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              
            </div>
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400">No heating devices detected.</div>
          )}
        </div>
      </Card>

      {/* 编辑弹窗（Heating） */}
      <ConfirmDialog
        open={heatEditOpen}
        title={`Edit ${heatEditMeta?.label ?? ''}`}
        onCancel={() => setHeatEditOpen(false)}
        onOk={handleConfirmHeatEdit}
        cancelText="Cancel"
        okText="Save"
        fullWidth
        maxWidth="sm"
      >
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={`${heatEditMeta?.label ?? ''}${heatEditMeta?.unit ? ` (${heatEditMeta.unit})` : ''}`}
          type="number"
          value={heatEditDraft}
          onChange={(e) => {
            const v = e.target.value;
            setHeatEditDraft(v);
            setHeatEditError(validateHeatValue(v));
          }}
          error={Boolean(heatEditError)}
          helperText={heatEditError}
          InputLabelProps={{ className: '!text-gray-700 dark:!text-gray-300' }}
          FormHelperTextProps={{ className: '!text-red-600 dark:!text-red-400' }}
          inputProps={{ step: 1, inputMode: 'numeric', pattern: '[0-9]*', className: '!text-gray-900 dark:!text-gray-100' }}
          className="dark:[&_.MuiOutlinedInput-notchedOutline]:!border-white dark:[&_.MuiOutlinedInput-root:hover_.MuiOutlinedInput-notchedOutline]:!border-white dark:[&_.MuiOutlinedInput-root.Mui-focused_.MuiOutlinedInput-notchedOutline]:!border-white"
        />
      </ConfirmDialog>
    </div>
  );
};

export default DeviceSettingsPage;
